import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { STATUS_LABELS, STATUS_OPTIONS } from './statuses';
import './Dashboard.css';

const Dashboard = () => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [counts, setCounts] = useState({});
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    position: '',
    search: '',
  });

  const fetchCounts = useCallback(async () => {
    try {
      const response = await api.get('/careers/counts');
      setCounts(response.data.counts);
      setPositions(response.data.positions);
    } catch (err) {
      console.error('Failed to fetch counts:', err);
    }
  }, []);

  const fetchApplications = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.position) params.append('position', filters.position);
      if (filters.search) params.append('search', filters.search);

      const response = await api.get(`/careers?${params.toString()}`);
      setApplications(response.data.applications);
      setError('');
    } catch (err) {
      setError('Failed to load applications');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchCounts();
    fetchApplications();
  }, [fetchCounts, fetchApplications]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getStatusBadge = (status) => (
    <span className={`status-badge status-${status}`}>
      {STATUS_LABELS[status] || status}
    </span>
  );

  return (
    <div className="careers-dashboard">
      <div className="dashboard-header">
        <div>
          <h1>Job Applications</h1>
          <p className="subtitle">Applications submitted from the careers page on fitness1stsports.com</p>
        </div>
      </div>

      <div className="stats-cards">
        <div className="stat-card stat-new">
          <div className="stat-value">{counts.new || 0}</div>
          <div className="stat-label">New</div>
        </div>
        <div className="stat-card stat-reviewing">
          <div className="stat-value">{counts.reviewing || 0}</div>
          <div className="stat-label">Reviewing</div>
        </div>
        <div className="stat-card stat-interviewing">
          <div className="stat-value">{counts.interviewing || 0}</div>
          <div className="stat-label">Interviewing</div>
        </div>
        <div className="stat-card stat-hired">
          <div className="stat-value">{counts.hired || 0}</div>
          <div className="stat-label">Hired</div>
        </div>
      </div>

      <div className="filters-bar">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by name, email, phone, or application #..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
          />
        </div>
        <select
          value={filters.position}
          onChange={(e) => handleFilterChange('position', e.target.value)}
        >
          <option value="">All Positions</option>
          {positions.map(p => (
            <option key={p.position} value={p.position}>
              {p.position} ({p.count})
            </option>
          ))}
        </select>
        <select
          value={filters.status}
          onChange={(e) => handleFilterChange('status', e.target.value)}
        >
          {STATUS_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="applications-table-container">
        {loading ? (
          <div className="loading">Loading applications...</div>
        ) : applications.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📝</div>
            <h3>No Applications Found</h3>
            <p>No job applications match your current filters.</p>
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <table className="applications-table">
              <thead>
                <tr>
                  <th>Application #</th>
                  <th>Applicant</th>
                  <th>Position</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Received</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {applications.map(app => (
                  <tr key={app.id} onClick={() => navigate(`/careers/${app.id}`)}>
                    <td className="application-number">{app.application_number}</td>
                    <td>
                      <div>{app.first_name} {app.last_name}</div>
                      <div className="applicant-email">{app.email}</div>
                    </td>
                    <td>{app.position}</td>
                    <td>{app.employment_type || '-'}</td>
                    <td>{getStatusBadge(app.status)}</td>
                    <td>{formatDate(app.created_at)}</td>
                    <td>
                      <button className="btn-view">View</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile Card View */}
            <div className="applications-mobile-list">
              {applications.map(app => (
                <div
                  key={app.id}
                  className="application-card"
                  onClick={() => navigate(`/careers/${app.id}`)}
                >
                  <div className="application-card-header">
                    <span className="application-card-number">{app.application_number}</span>
                    {getStatusBadge(app.status)}
                  </div>
                  <div className="application-card-body">
                    <div className="application-card-name">{app.first_name} {app.last_name}</div>
                    <div className="application-card-contact">{app.email} • {app.phone}</div>
                    <div className="application-card-row">
                      <span className="application-card-meta">{app.position}</span>
                      <span className="application-card-date">{formatDate(app.created_at)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
